import { z } from "zod";
import {
  queryKeys,
  withAuthQueryScope,
  type AuthQueryScope,
} from "~/lib/query-keys";
import type { PlatformApiTrpcClient } from "~/lib/trpc/platform-api-adapter";
import { formatUnits } from "~/lib/unit-conversion";
import { toExecutionEvmAsset } from "./portfolio-execution-utils";
import type { WithdrawableExecutionAsset } from "./withdrawable-assets";

const VaultTokenBalanceSchema = z.object({
  assetId: z.string().min(1),
  balanceInHumanReadableAmount: z.string(),
  decimals: z.number().int().min(0),
  name: z.string().min(1),
  symbol: z.string().min(1),
  tokenIcon: z.string().trim().min(1).nullable().optional(),
  tokenPriceInUsd: z.union([z.string(), z.number()]).optional(),
  totalBalance: z.string().regex(/^\d+$/u),
});

const VaultPortfolioDataSchema = z.object({
  portfolioData: z.object({
    tokenBalancesWithUsdPrices: z.array(VaultTokenBalanceSchema),
  }),
});

function readEvmChainRef(assetId: string): string | null {
  const caipMatch = /^eip155:(\d+)\//u.exec(assetId.trim());
  if (caipMatch?.[1]) {
    return caipMatch[1];
  }

  const legacyMatch = /:(\d+)$/u.exec(assetId.trim());
  return legacyMatch?.[1] ?? null;
}

function readPositivePriceUsd(value: string | number | undefined) {
  const priceUsd =
    typeof value === "number" ? value : Number.parseFloat(value ?? "");
  return Number.isFinite(priceUsd) && priceUsd > 0 ? priceUsd : undefined;
}

/**
 * Parses the deliberately loose `v2.portfolio.vaults.list` response and maps
 * its RPC-backed balances into execution assets. `totalBalance` is the source
 * of truth: display-formatted decimal strings never participate in amount
 * validation or Max calculations.
 */
export function parseVaultPortfolioWithdrawableAssets(
  payload: unknown,
): WithdrawableExecutionAsset[] {
  const parsed = VaultPortfolioDataSchema.parse(payload);

  return parsed.portfolioData.tokenBalancesWithUsdPrices.flatMap((balance) => {
    const chainRef = readEvmChainRef(balance.assetId);
    if (!chainRef) {
      return [];
    }

    const executionAsset = toExecutionEvmAsset({
      assetId: balance.assetId,
      chainRef,
    });
    if (!executionAsset) {
      return [];
    }

    const rawQuantity = BigInt(balance.totalBalance);
    if (rawQuantity <= 0n) {
      return [];
    }

    const priceUsd = readPositivePriceUsd(balance.tokenPriceInUsd);
    return [
      {
        assetId: balance.assetId,
        chainId: executionAsset.chainId,
        chainRef,
        decimals: balance.decimals,
        executionAssetId: executionAsset.assetId,
        ...(balance.tokenIcon ? { imageUrl: balance.tokenIcon } : {}),
        name: balance.name,
        ...(priceUsd === undefined ? {} : { priceUsd }),
        quantity: formatUnits(rawQuantity, balance.decimals),
        rawQuantity: balance.totalBalance,
        symbol: balance.symbol,
        // readEvmChainRef filtered to eip155 assets above.
        vm: "evm" as const,
      },
    ];
  });
}

export function getVaultPortfolioWithdrawableAssetsQueryOptions(input: {
  authQueryScope: AuthQueryScope;
  platformApi: PlatformApiTrpcClient;
  portfolioId: string | null;
}) {
  return {
    gcTime: 0,
    queryFn: async () => {
      if (!input.portfolioId) {
        return [];
      }

      const response = await input.platformApi.v2.portfolio.vaults.list.query({
        portfolioId: input.portfolioId,
      });
      return parseVaultPortfolioWithdrawableAssets(response);
    },
    queryKey: withAuthQueryScope(
      queryKeys.portfolio.withdrawableLiveAssetsById(
        input.portfolioId ?? "missing",
      ),
      input.authQueryScope,
    ),
    refetchInterval: 30_000,
    retry: 1,
    staleTime: 0,
  } as const;
}
